import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { IosShareOutlined as IosShareOutlinedIcon } from "../components/icons/FigmaControlIcons";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import PublicOutlinedIcon from "@mui/icons-material/PublicOutlined";
import { buildApiProxyUrl } from "../utils/apiUrls";
import PublicJobProfile from "../components/PublicJobProfile";
import PublicLeadProfile from "../components/PublicLeadProfile";

const API_BASE = String(process.env.REACT_APP_API_BASE || "").trim();
const PURPLE = "#9C4FFF";
const BLUE = "#3451F4";

function cleanText(value) {
  if (value === undefined || value === null) return "";
  return String(value).trim();
}

function entityHeading(entityLabel) {
  const normalized = cleanText(entityLabel);
  return normalized ? `${normalized} Shared Link` : "Shared Link";
}

export default function PublicSharePage() {
  const { token = "" } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [share, setShare] = useState(null);

  useEffect(() => {
    if (!token) {
      setLoading(false);
      setError("This shared page link is invalid.");
      return;
    }

    let active = true;
    setLoading(true);
    setError("");

    fetch(buildApiProxyUrl(`/public/share-links/${encodeURIComponent(token)}`, API_BASE))
      .then(async (response) => {
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(payload?.error || "Failed to load the shared page.");
        }
        return payload;
      })
      .then((payload) => {
        if (!active) return;
        setShare(payload?.share || null);
      })
      .catch((fetchError) => {
        if (!active) return;
        setError(fetchError?.message || "Failed to load the shared page.");
      })
      .finally(() => {
        if (!active) return;
        setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [token]);

  const pageTitle = useMemo(() => entityHeading(share?.entityLabel), [share?.entityLabel]);

  useEffect(() => {
    if (typeof document === "undefined") return;
    document.title = share?.title
      ? `${share.title} | ${pageTitle}`
      : pageTitle;
  }, [pageTitle, share?.title]);

  if (!loading && !error && share?.entityType === "job" && share.job) {
    return <PublicJobProfile key={token} job={share.job} token={token} apiBase={API_BASE} />;
  }
  if (!loading && !error && share?.entityType === "lead") {
    return <PublicLeadProfile share={share} />;
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background:
          "radial-gradient(circle at top left, rgba(156,79,255,0.14), transparent 28%), radial-gradient(circle at bottom right, rgba(52,81,244,0.12), transparent 26%), linear-gradient(180deg, #F8FAFF 0%, #EEF2FF 100%)",
        px: { xs: 2, sm: 3, md: 5 },
        py: { xs: 3, md: 5 },
      }}
    >
      <Box sx={{ maxWidth: 920, mx: "auto" }}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={2}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", md: "center" }}
          sx={{ mb: 3.5 }}
        >
          <Stack direction="row" spacing={1.25} alignItems="center">
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: "14px",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#fff",
                background: "linear-gradient(135deg, #3451F4 0%, #9C4FFF 100%)",
                boxShadow: "0 14px 28px rgba(52, 81, 244, 0.2)",
              }}
            >
              <IosShareOutlinedIcon sx={{ fontSize: 20 }} />
            </Box>

            <Box>
              <Typography
                sx={{
                  fontFamily: "Be Vietnam Pro",
                  fontSize: 13,
                  fontWeight: 800,
                  textTransform: "uppercase",
                  color: BLUE,
                }}
              >
                Shared Through RPO AI
              </Typography>
              <Typography
                sx={{
                  fontFamily: "Be Vietnam Pro",
                  fontSize: { xs: 20, sm: 24 },
                  fontWeight: 800,
                  color: "#101828",
                }}
              >
                {pageTitle}
              </Typography>
            </Box>
          </Stack>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
            <Chip
              icon={<PublicOutlinedIcon sx={{ color: `${BLUE} !important` }} />}
              label="No login required"
              sx={{
                height: 36,
                borderRadius: "999px",
                backgroundColor: "#FFFFFF",
                border: "1px solid rgba(52,81,244,0.12)",
                color: BLUE,
                fontFamily: "Be Vietnam Pro",
                fontWeight: 700,
              }}
            />
            <Chip
              icon={<LockOutlinedIcon sx={{ color: `${PURPLE} !important` }} />}
              label="Read-only access"
              sx={{
                height: 36,
                borderRadius: "999px",
                backgroundColor: "#FFFFFF",
                border: "1px solid rgba(156,79,255,0.14)",
                color: PURPLE,
                fontFamily: "Be Vietnam Pro",
                fontWeight: 700,
              }}
            />
          </Stack>
        </Stack>

        {loading ? (
          <Paper
            elevation={0}
            sx={{
              minHeight: 320,
              borderRadius: "28px",
              border: "1px solid rgba(15, 23, 42, 0.08)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexDirection: "column",
              gap: 2,
            }}
          >
            <CircularProgress size={32} sx={{ color: PURPLE }} />
            <Typography sx={{ fontFamily: "Be Vietnam Pro", color: "#475467" }}>
              Loading shared page...
            </Typography>
          </Paper>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : share ? (
          <Paper
            elevation={0}
            sx={{
              borderRadius: "28px",
              border: "1px solid rgba(15, 23, 42, 0.08)",
              overflow: "hidden",
              backgroundColor: "#FFFFFF",
            }}
          >
            <Box
              sx={{
                px: { xs: 2.5, sm: 3.5 },
                py: { xs: 2.5, sm: 3 },
                background:
                  "linear-gradient(135deg, rgba(52,81,244,0.08) 0%, rgba(156,79,255,0.12) 100%)",
                borderBottom: "1px solid rgba(208,213,221,0.7)",
              }}
            >
              <Stack spacing={1.5}>
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={1}
                  justifyContent="space-between"
                  alignItems={{ xs: "flex-start", sm: "center" }}
                >
                  <Typography
                    sx={{
                      fontFamily: "Be Vietnam Pro",
                      fontSize: { xs: 24, sm: 30 },
                      fontWeight: 800,
                      color: "#101828",
                    }}
                  >
                    {share.title}
                  </Typography>
                  {share.badge ? (
                    <Chip
                      label={share.badge}
                      sx={{
                        height: 32,
                        borderRadius: "999px",
                        backgroundColor: "#FFFFFF",
                        color: PURPLE,
                        border: "1px solid rgba(156,79,255,0.20)",
                        fontWeight: 700,
                        fontFamily: "Be Vietnam Pro",
                      }}
                    />
                  ) : null}
                </Stack>

                {share.subtitle ? (
                  <Typography
                    sx={{
                      fontFamily: "Be Vietnam Pro",
                      fontSize: 16,
                      fontWeight: 600,
                      color: "#344054",
                    }}
                  >
                    {share.subtitle}
                  </Typography>
                ) : null}

                {Array.isArray(share.chips) && share.chips.length ? (
                  <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                    {share.chips.map((chip) => (
                      <Chip
                        key={chip}
                        label={chip}
                        size="small"
                        sx={{
                          borderRadius: "999px",
                          backgroundColor: "#FFFFFF",
                          border: "1px solid #D0D5DD",
                          color: "#344054",
                          fontFamily: "Be Vietnam Pro",
                          fontWeight: 600,
                        }}
                      />
                    ))}
                  </Stack>
                ) : null}
              </Stack>
            </Box>

            <Box sx={{ px: { xs: 2.5, sm: 3.5 }, py: { xs: 2.5, sm: 3 } }}>
              {share.description ? (
                <Typography
                  sx={{
                    fontFamily: "Be Vietnam Pro",
                    fontSize: 14,
                    lineHeight: 1.75,
                    color: "#475467",
                  }}
                >
                  {share.description}
                </Typography>
              ) : null}

              {share.description && Array.isArray(share.fields) && share.fields.length ? (
                <Divider sx={{ my: 3 }} />
              ) : null}

              {Array.isArray(share.fields) && share.fields.length ? (
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
                    gap: 2,
                  }}
                >
                  {share.fields.map((field) => (
                    <Box
                      key={`${field.label}-${field.value}`}
                      sx={{
                        p: 2,
                        borderRadius: "18px",
                        border: "1px solid #EAECF0",
                        backgroundColor: "#FCFCFD",
                        gridColumn: share.entityType === "lead" && ["Description", "Key Responsibilities", "Qualifications"].includes(field.label) ? "1 / -1" : undefined,
                        minWidth: 0,
                      }}
                    >
                      <Typography
                        sx={{
                          fontFamily: "Be Vietnam Pro",
                          fontSize: 12,
                          fontWeight: 700,
                          color: BLUE,
                          mb: 0.75,
                          textTransform: "uppercase",
                        }}
                      >
                        {field.label}
                      </Typography>
                      <Typography
                        sx={{
                          fontFamily: "Be Vietnam Pro",
                          fontSize: 14,
                          fontWeight: 600,
                          color: "#101828",
                          lineHeight: 1.6,
                          whiteSpace: "pre-wrap",
                          overflowWrap: "anywhere",
                        }}
                      >
                        {field.value}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              ) : null}

              {Array.isArray(share.documents) && share.documents.length ? (
                <>
                  <Divider sx={{ my: 3 }} />
                  <Typography sx={{ fontFamily: "Be Vietnam Pro", fontSize: 14, fontWeight: 800, color: "#101828", mb: 1.25 }}>
                    Documents
                  </Typography>
                  <Stack spacing={1}>
                    {share.documents.map((document) => (
                      <Box
                        key={`${document.name}-${document.url}`}
                        component="a"
                        href={document.url}
                        target="_blank"
                        rel="noreferrer"
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 2,
                          p: 1.5,
                          borderRadius: "14px",
                          border: "1px solid #EAECF0",
                          color: BLUE,
                          textDecoration: "none",
                          fontFamily: "Be Vietnam Pro",
                          fontWeight: 700,
                          "&:hover": { borderColor: PURPLE, backgroundColor: "#FAF7FF" },
                        }}
                      >
                        <span>{document.name}</span>
                        <span>Open</span>
                      </Box>
                    ))}
                  </Stack>
                </>
              ) : null}
            </Box>
          </Paper>
        ) : (
          <Alert severity="error">This shared page link is invalid.</Alert>
        )}
      </Box>
    </Box>
  );
}
